import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// What a browser shows by itself. HEIC is missing on purpose: a local HEIC has no preview
// (an Ablage HEIC does, through a server-side variant — BillingAttachmentPreviewHelper).
const BROWSER_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"]

// Versanddialog eines Belegs oder einer Mahnung (Billing::SendDialogComponent).
//
// Anlagen kommen aus zwei Quellen und landen in einer Liste über den Tabs, die mit dem
// Formular geht:
//   - Dokumentenablage: eine Seite Dateien im Turbo Frame "picker" (gesucht, gefiltert und
//     sortiert auf dem Server). Die Checkboxen dort haben keinen Namen; eine gewählte Datei
//     wird als Zeile mit verstecktem attachment_file_ids[] übernommen, damit die Auswahl
//     Suche und Blättern übersteht. Nach jedem Laden des Frames werden die Häkchen wieder
//     gesetzt (syncPicker).
//   - Vom Computer: jede Datei geht sofort per Direct Upload in den Speicher
//     (Billing::EmailUploadsController legt den Blob an); die Zeile trägt danach
//     upload_signed_ids[]. Ein Absenden während eines Uploads wird gehalten und folgt,
//     sobald der letzte fertig ist — nach einem Fehler nicht, dann entscheidet der Nutzer.
//
// Die Fußzeile schätzt die Mailgröße und warnt über dem Limit; der Server prüft dasselbe
// beim Senden (Billing::EmailAttachmentParams). Zusammengeführt zählen Bilder verkleinert
// (data-merged-size); was PdfAnnex nicht lesen kann, geht immer einzeln und in voller Größe.
//
// Vorschau: PDFs und Bilder öffnen in der Lightbox des Dokumentbrowsers (document-preview).
// Ablage-Dateien bringen ihre URLs aus der Auswahl mit (data-preview-*), ein Upload zeigt die
// lokale Datei über eine Object-URL, die mit der Zeile wieder freigegeben wird.
export default class extends Controller {
  static targets = [
    "selectedList",
    "rowTemplate",
    "summary",
    "meter",
    "meterLabel",
    "warning",
    "waiting",
    "failedHint",
    "preview",
    "modeGroup",
    "mode",
    "tab",
    "panel",
    "query",
    "folder",
    "source",
    "sort",
    "picker",
    "file",
    "fileInput",
    "dropzone",
    "submit",
  ]

  static values = {
    limit: Number,
    mergedImageBytes: Number,
    imageTypes: Array,
    mergeableTypes: Array,
    previewUrl: String,
    pickerUrl: String,
    uploadUrl: String,
    texts: Object,
  }

  connect() {
    this.pendingUploads = 0
    this.uploadSeq = 0
    this.emptySummary = this.summaryTarget.textContent
    this.update()
  }

  disconnect() {
    clearTimeout(this.searchTimer)
    this.rows().forEach((row) => this.releasePreview(row))
  }

  // --- Tabs -------------------------------------------------------------------------

  showTab(event) {
    const key = event.currentTarget.dataset.panel
    this.tabTargets.forEach((tab) =>
      tab.setAttribute("aria-selected", String(tab.dataset.panel === key)),
    )
    this.panelTargets.forEach((panel) =>
      panel.classList.toggle("hidden", panel.dataset.panel !== key),
    )
  }

  // --- Dokumentenablage ---------------------------------------------------------------

  search() {
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this.filter(), 300)
  }

  // Enter in the search field must not submit the send form.
  searchNow(event) {
    event.preventDefault()
    clearTimeout(this.searchTimer)
    this.filter()
  }

  filter() {
    if (!this.hasPickerTarget || !this.pickerUrlValue) return

    const url = new URL(this.pickerUrlValue, window.location.origin)
    const params = {
      q: this.hasQueryTarget ? this.queryTarget.value.trim() : "",
      folder_id: this.hasFolderTarget ? this.folderTarget.value : "",
      source: this.hasSourceTarget ? this.sourceTarget.value : "",
      sort: this.hasSortTarget ? this.sortTarget.value : "",
    }
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value)
    })
    this.pickerTarget.src = `${url.pathname}${url.search}`
  }

  syncPicker() {
    this.fileTargets.forEach((input) => {
      input.checked = Boolean(this.rowFor("file", input.value))
    })
  }

  toggleFile(event) {
    const input = event.currentTarget
    if (input.checked) {
      if (!this.rowFor("file", input.value)) {
        const row = this.addRow({
          kind: "file",
          key: input.value,
          name: input.dataset.filename,
          detail: [this.textsValue.origin_ablage, input.dataset.detail].filter(Boolean).join(" · "),
          size: Number(input.dataset.size),
          mergedSize: Number(input.dataset.mergedSize),
          contentType: input.dataset.contentType,
          preview: input.dataset.previewKind
            ? {
                kind: input.dataset.previewKind,
                url: input.dataset.previewUrl,
                downloadUrl: input.dataset.previewDownloadUrl,
              }
            : null,
        })
        row.append(this.hiddenInput("attachment_file_ids[]", input.value))
      }
    } else {
      this.rowFor("file", input.value)?.remove()
    }
    this.update()
  }

  // --- Vom Computer -------------------------------------------------------------------

  pickFiles(event) {
    const files = Array.from(event.target.files || [])
    event.target.value = null
    files.forEach((file) => this.upload(file))
  }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-green-500", "bg-green-50")
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")
  }

  drop(event) {
    event.preventDefault()
    this.dragLeave()
    Array.from(event.dataTransfer?.files || []).forEach((file) => this.upload(file))
  }

  upload(file) {
    const mergeable = this.mergeableTypesValue.includes(file.type)
    const row = this.addRow({
      kind: "upload",
      key: `u${++this.uploadSeq}`,
      name: file.name,
      detail: mergeable
        ? this.textsValue.origin_upload
        : `${this.textsValue.origin_upload} · ${this.textsValue.separate_only}`,
      size: file.size,
      mergedSize: this.imageTypesValue.includes(file.type)
        ? Math.min(file.size, this.mergedImageBytesValue)
        : file.size,
      contentType: file.type,
      preview: this.localPreview(file),
    })
    if (file.size > this.limitValue) {
      this.fail(row, this.textsValue.too_large)
      return
    }

    row.dataset.pending = "true"
    this.part(row, "progress").classList.remove("hidden")
    this.pendingUploads += 1
    this.update()

    const delegate = {
      directUploadWillStoreFileWithXHR: (xhr) => {
        // Removed while the blob was still being created: don't store the file at all.
        if (!row.isConnected) {
          xhr.addEventListener("loadstart", () => xhr.abort(), { once: true })
          return
        }
        row.uploadRequest = xhr
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            this.part(row, "progressBar").style.width =
              `${Math.round((event.loaded / event.total) * 100)}%`
          }
        })
      },
    }
    new DirectUpload(file, this.uploadUrlValue, delegate).create((error, blob) =>
      this.uploaded(row, error, blob),
    )
  }

  uploaded(row, error, blob) {
    if (!this.settle(row)) return

    if (row.isConnected) {
      if (error) {
        this.fail(row, this.textsValue.upload_failed)
      } else {
        this.part(row, "progress").classList.add("hidden")
        row.dataset.signedId = blob.signed_id
        row.append(this.hiddenInput("upload_signed_ids[]", blob.signed_id))
      }
    }
    this.update()
    this.releaseHeldSubmit()
  }

  // Counts a pending upload as finished, once: either its callback or its removal does it.
  // An aborted XHR fires neither load nor error, so @rails/activestorage never calls back
  // for a row removed mid-upload — without this the send would stay held for good.
  settle(row) {
    if (row.dataset.pending !== "true") return false

    delete row.dataset.pending
    this.pendingUploads -= 1
    return true
  }

  fail(row, message) {
    row.dataset.failed = "true"
    const error = this.part(row, "error")
    error.textContent = message
    error.classList.remove("hidden")
    this.part(row, "progress").classList.add("hidden")
    this.update()
  }

  // --- Liste ----------------------------------------------------------------------------

  remove(event) {
    const row = event.currentTarget.closest('[data-role="row"]')
    if (!row) return

    if (row.dataset.kind === "file") {
      const input = this.fileTargets.find((candidate) => candidate.value === row.dataset.key)
      if (input) input.checked = false
    }
    this.settle(row)
    row.uploadRequest?.abort()
    this.releasePreview(row)
    row.remove()
    this.update()
    this.releaseHeldSubmit()
  }

  addRow({ kind, key, name, detail, size, mergedSize, contentType, preview }) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    Object.assign(row.dataset, { kind, key, size, mergedSize, contentType: contentType || "" })
    this.part(row, "name").textContent = name
    this.part(row, "name").title = name
    this.part(row, "detail").textContent = detail
    this.part(row, "size").textContent = this.humanSize(size)
    this.part(row, "remove").setAttribute("aria-label", `${this.textsValue.remove}: ${name}`)
    if (preview) this.showPreview(row, name, preview)

    // Ablage files before uploads: the order the merged PDF gets them in.
    const firstUpload =
      kind === "file" ? this.selectedListTarget.querySelector('[data-kind="upload"]') : null
    this.selectedListTarget.insertBefore(row, firstUpload)
    return row
  }

  // The row's document-preview controller reads its values when opened, so setting them
  // after the row is cloned is enough.
  showPreview(row, name, { kind, url, downloadUrl, objectUrl }) {
    const wrapper = this.part(row, "preview")
    if (!wrapper) return

    wrapper.dataset.documentPreviewKindValue = kind
    wrapper.dataset.documentPreviewUrlValue = url
    wrapper.dataset.documentPreviewDownloadUrlValue = downloadUrl || url
    wrapper.dataset.documentPreviewFilenameValue = name
    const label = this.textsValue.preview_label.replace("__NAME__", name)
    wrapper.querySelector('[data-role="previewButton"]')?.setAttribute("aria-label", label)
    wrapper.classList.remove("invisible")
    if (objectUrl) row.previewObjectUrl = objectUrl
  }

  localPreview(file) {
    let kind = null
    if (file.type === "application/pdf") kind = "pdf"
    else if (BROWSER_IMAGE_TYPES.includes(file.type)) kind = "image"
    if (!kind || typeof URL.createObjectURL !== "function") return null

    const url = URL.createObjectURL(file)
    return { kind, url, downloadUrl: url, objectUrl: url }
  }

  releasePreview(row) {
    if (row.previewObjectUrl) URL.revokeObjectURL(row.previewObjectUrl)
    row.previewObjectUrl = null
  }

  rowFor(kind, key) {
    return this.rows().find((row) => row.dataset.kind === kind && row.dataset.key === String(key))
  }

  rows() {
    return Array.from(this.selectedListTarget.querySelectorAll('[data-role="row"]'))
  }

  // Rows that will reach the mail (failed uploads won't).
  activeRows() {
    return this.rows().filter((row) => row.dataset.failed !== "true")
  }

  // A failed upload stays in the list until the user removes it; sending past it would
  // mail the customer without a file the sender believes is attached.
  failedRows() {
    return this.rows().filter((row) => row.dataset.failed === "true")
  }

  // --- Senden ---------------------------------------------------------------------------

  submit(event) {
    if (this.failedRows().length > 0) {
      event.preventDefault()
      return
    }
    if (this.pendingUploads === 0) return

    event.preventDefault()
    this.heldSubmit = true
    this.waitingTarget.classList.remove("hidden")
    this.update()
  }

  releaseHeldSubmit() {
    if (!this.heldSubmit || this.pendingUploads > 0) return

    this.heldSubmit = false
    this.waitingTarget.classList.add("hidden")
    this.update()
    if (this.failedRows().length === 0) this.element.requestSubmit()
  }

  // Öffnet das zusammengeführte PDF so, wie der Kunde es bekäme.
  preview() {
    const url = new URL(this.previewUrlValue, window.location.origin)
    this.activeRows().forEach((row) => {
      if (row.dataset.kind === "file")
        url.searchParams.append("attachment_file_ids[]", row.dataset.key)
      else if (row.dataset.signedId)
        url.searchParams.append("upload_signed_ids[]", row.dataset.signedId)
    })
    window.open(url.toString(), "_blank", "noopener")
  }

  // --- Anzeige --------------------------------------------------------------------------

  update() {
    const rows = this.activeRows()
    const merged = this.merged()
    const bytes = rows.reduce(
      (sum, row) =>
        sum + Number(merged && this.mergeable(row) ? row.dataset.mergedSize : row.dataset.size),
      0,
    )
    const tooLarge = bytes > this.limitValue

    this.summaryTarget.textContent = rows.length
      ? this.textsValue.summary
          .replace("__FILES__", rows.length)
          .replace("__SIZE__", this.humanSize(bytes))
      : this.emptySummary
    this.meterLabelTarget.textContent = rows.length
      ? `${this.humanSize(bytes)} / ${this.humanSize(this.limitValue)}`
      : ""
    this.meterTarget.style.width = `${Math.min(100, (bytes / this.limitValue) * 100)}%`
    this.meterTarget.classList.toggle("bg-green-500", !tooLarge)
    this.meterTarget.classList.toggle("bg-amber-500", tooLarge)
    this.warningTarget.classList.toggle("hidden", !tooLarge)
    this.modeGroupTarget.classList.toggle("hidden", rows.length === 0)

    const failed = this.failedRows().length > 0
    if (this.hasFailedHintTarget) this.failedHintTarget.classList.toggle("hidden", !failed)
    this.submitTarget.disabled = failed || Boolean(this.heldSubmit)

    // invisible, not hidden: a link appearing would shift the radios under the pointer. Over
    // the limit the server refuses the preview too; a running upload has no signed id yet.
    if (this.hasPreviewTarget) {
      const previewable =
        merged && rows.some((row) => this.mergeable(row)) && !tooLarge && this.pendingUploads === 0
      this.previewTarget.classList.toggle("invisible", !previewable)
    }
  }

  // Ohne Wahlmöglichkeit (E-Rechnung) gehen die Anlagen immer einzeln mit.
  merged() {
    const selected = this.modeTargets.find((input) => input.checked)
    return selected ? selected.value === "merged" : false
  }

  mergeable(row) {
    return this.mergeableTypesValue.includes(row.dataset.contentType)
  }

  part(row, role) {
    return row.querySelector(`[data-role="${role}"]`)
  }

  hiddenInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  humanSize(bytes) {
    const megabytes = bytes / (1024 * 1024)
    if (megabytes >= 1) return `${megabytes.toFixed(1).replace(".", ",")} MB`
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }
}
